/**
 * @fileoverview File Upload Progress Component
 * 
 * Displays real-time upload progress with individual file status,
 * progress bars, and estimated time remaining.
 * 
 * Requirements: 1.4, 1.5
 */

import React, { useMemo } from 'react';
import { useModuleContext } from '../../context/ModuleContextProvider.jsx';
import { formatFileSize } from '../types.js';

/**
 * File Upload Progress Component
 * 
 * @param {Object} props - Component props
 * @param {UploadFile[]} props.uploads - Array of upload objects
 * @param {string} [props.testId] - Test ID
 */
export const FileUploadProgress = ({ 
  uploads, 
  testId = 'file-upload-progress' 
}) => {
  const { theme } = useModuleContext();
  const isDark = theme?.mode === 'dark';

  // Calculate overall progress statistics
  const progressStats = useMemo(() => {
    const total = uploads.length;
    const completed = uploads.filter(u => u.status === 'completed').length;
    const failed = uploads.filter(u => u.status === 'failed').length;
    const uploading = uploads.filter(u => u.status === 'uploading').length;
    const pending = uploads.filter(u => u.status === 'pending').length;

    const overallProgress = total > 0 
      ? uploads.reduce((sum, upload) => sum + upload.progress, 0) / total
      : 0;

    const totalSize = uploads.reduce((sum, upload) => sum + upload.file.size, 0);
    const uploadedSize = uploads.reduce((sum, upload) => {
      return sum + (upload.loaded || 0);
    }, 0);

    // Calculate estimated time remaining
    const activeUploads = uploads.filter(u => u.status === 'uploading' && u.estimatedTimeRemaining);
    const maxTimeRemaining = activeUploads.length > 0
      ? Math.max(...activeUploads.map(u => u.estimatedTimeRemaining))
      : 0;

    return {
      total,
      completed,
      failed,
      uploading,
      pending,
      overallProgress: Math.round(overallProgress),
      totalSize,
      uploadedSize,
      estimatedTimeRemaining: maxTimeRemaining
    };
  }, [uploads]);

  /**
   * Format time remaining for display
   */
  const formatTimeRemaining = (ms) => {
    if (!ms || ms <= 0) return '';
    
    const seconds = Math.ceil(ms / 1000);
    if (seconds < 60) return `${seconds}s remaining`;
    
    const minutes = Math.ceil(seconds / 60);
    if (minutes < 60) return `${minutes}m remaining`;
    
    const hours = Math.ceil(minutes / 60);
    return `${hours}h remaining`;
  };

  /**
   * Get status icon for upload
   */
  const getStatusIcon = (upload) => {
    switch (upload.status) {
      case 'pending':
        return (
          <div className={`w-4 h-4 rounded-full border-2 ${
            isDark ? 'border-gray-500' : 'border-gray-300'
          }`} />
        );
      
      case 'uploading':
        return (
          <div className="w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
        );
      
      case 'completed':
        return (
          <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path 
              fillRule="evenodd" 
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
              clipRule="evenodd" 
            />
          </svg>
        );
      
      case 'failed':
        return (
          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path 
              fillRule="evenodd" 
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" 
              clipRule="evenodd" 
            />
          </svg>
        );
      
      default:
        return null;
    }
  };

  /**
   * Get status text color
   */
  const getStatusTextColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-500';
      case 'uploading':
        return isDark ? 'text-blue-400' : 'text-blue-600';
      default:
        return isDark ? 'text-gray-400' : 'text-gray-500';
    }
  };

  if (uploads.length === 0) {
    return null;
  }

  return (
    <div 
      className={`file-upload-progress mt-6 p-4 rounded-lg border ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
      data-testid={testId}
    >
      {/* Progress Header */}
      <div className="progress-header mb-4">
        <div className="flex items-center justify-between">
          <h4 className={`text-sm font-medium ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Upload Progress ({progressStats.completed}/{progressStats.total})
          </h4>
          
          {progressStats.estimatedTimeRemaining > 0 && (
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {formatTimeRemaining(progressStats.estimatedTimeRemaining)}
            </span>
          )}
        </div>

        {/* Overall Progress Bar */}
        <div className="mt-2">
          <div className={`w-full rounded-full h-2 ${
            isDark ? 'bg-gray-700' : 'bg-gray-200'
          }`}>
            <div 
              className="h-2 rounded-full transition-all duration-300 bg-teal-600"
              style={{ width: `${progressStats.overallProgress}%` }}
              role="progressbar"
              aria-valuenow={progressStats.overallProgress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Overall upload progress: ${progressStats.overallProgress}%`}
            />
          </div>
          
          <div className="flex justify-between mt-1">
            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {formatFileSize(progressStats.uploadedSize)} / {formatFileSize(progressStats.totalSize)}
            </span>
            <span className={`text-xs font-medium ${
              progressStats.overallProgress === 100 
                ? 'text-green-600' 
                : isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {progressStats.overallProgress}%
            </span>
          </div>
        </div>
      </div>

      {/* Individual File Progress */}
      <div className="file-progress-list space-y-3">
        {uploads.map((upload) => (
          <div key={upload.id} className="file-progress-item">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                {/* Status Icon */}
                <div className="flex-shrink-0">
                  {getStatusIcon(upload)}
                </div>
                
                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {upload.file.name}
                  </p>
                  <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formatFileSize(upload.file.size)}
                    {upload.status === 'uploading' && upload.estimatedTimeRemaining && (
                      <span> • {formatTimeRemaining(upload.estimatedTimeRemaining)}</span>
                    )}
                    {upload.status === 'failed' && upload.error && (
                      <span className="text-red-500"> • {upload.error}</span>
                    )}
                    {upload.status === 'completed' && upload.endTime && upload.startTime && (
                      <span className="text-green-600">
                        {' • '}Completed in {Math.round((upload.endTime - upload.startTime) / 1000)}s
                      </span>
                    )}
                    {upload.retryCount > 0 && (
                      <span className={`${getStatusTextColor(upload.status)}`}>
                        {' • '}Retry {upload.retryCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Progress Percentage */}
              {(upload.status === 'uploading' || upload.status === 'completed') && (
                <span className={`text-sm font-medium ${getStatusTextColor(upload.status)}`}>
                  {Math.round(upload.progress)}%
                </span>
              )}
            </div>
            
            {/* Individual Progress Bar */}
            {(upload.status === 'uploading' || upload.status === 'completed') && (
              <div className={`w-full rounded-full h-1.5 ${
                isDark ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                <div 
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    upload.status === 'completed' 
                      ? 'bg-green-500' 
                      : 'bg-teal-600'
                  }`}
                  style={{ width: `${upload.progress}%` }}
                  role="progressbar"
                  aria-valuenow={upload.progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${upload.file.name} upload progress: ${upload.progress}%`}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Progress Summary */}
      {progressStats.total > 1 && (
        <div className={`mt-4 pt-3 border-t ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex justify-between items-center text-sm">
            <div className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {progressStats.completed > 0 && (
                <span className="text-green-600">
                  {progressStats.completed} completed
                </span>
              )}
              {progressStats.uploading > 0 && (
                <span className={`${progressStats.completed > 0 ? 'ml-2' : ''} text-blue-600`}>
                  {progressStats.uploading} uploading
                </span>
              )}
              {progressStats.failed > 0 && (
                <span className={`${(progressStats.completed > 0 || progressStats.uploading > 0) ? 'ml-2' : ''} text-red-500`}>
                  {progressStats.failed} failed
                </span>
              )}
              {progressStats.pending > 0 && (
                <span className={`${(progressStats.completed > 0 || progressStats.uploading > 0 || progressStats.failed > 0) ? 'ml-2' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {progressStats.pending} pending
                </span>
              )}
            </div>
            
            <div className={`font-medium ${
              progressStats.overallProgress === 100 
                ? 'text-green-600' 
                : isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {progressStats.overallProgress}% Complete
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
